'use client';
import styled, { css } from 'styled-components';
import { StarRating as SharedStarRating } from '../StarRating';

export const StyledStoryCardsSection = styled.section`
  width: 100%;
  max-width: 1536px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing(8, 4)};

  @media ${props => props.theme.laptop} {
    padding: ${props => props.theme.spacing(12, 8)};
  }
`;

export const AnimatedRegion = styled.div`
  opacity: 1;
  transform: none;
  transition: none;
`;

export const ContentColumn = styled(AnimatedRegion)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media ${props => props.theme.laptop} {
    grid-row: 1;
    align-items: flex-start;
    text-align: left;
    gap: ${props => props.theme.spacing(5)};
    grid-column: ${props =>
      props.$layoutDirection === 'content-left' ? 1 : 2};
  }
`;

export const ContentIntro = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${props => props.theme.spacing(8)};
  width: 100%;

  @media ${props => props.theme.laptop} {
    align-items: flex-start;
    text-align: left;
    gap: ${props => props.theme.spacing(5)};
  }
`;

export const CardsColumn = styled(AnimatedRegion)`
  width: 100%;
  min-width: 0;

  @media ${props => props.theme.laptop} {
    grid-row: 1;
    grid-column: ${props =>
      props.$layoutDirection === 'content-left' ? 2 : 1};
  }
`;

export const LayoutGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(12)};

  @media ${props => props.theme.laptop} {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: ${props => props.theme.spacing(10)};
    align-items: center;

    ${props =>
      props.$layoutDirection === 'content-left' &&
      css`
        grid-template-columns: 1fr 2fr;
      `}
  }
`;

export const StarRatingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing(2.5)};

  @media ${props => props.theme.laptop} {
    justify-content: flex-start;
  }
`;

export const StyledStarRating = styled(SharedStarRating)`
  flex-shrink: 0;

  &::before {
    background-color: ${props => props.theme.colors.accent.gold};
  }
`;

export const ReviewCountLabel = styled.span`
  font-size: 20px;
  line-height: 1.2;
  font-weight: 700;
  color: ${props => props.theme.colors.get(props.$fontColor)};
`;

export const SubheadlineContainer = styled.div`
  color: ${props => props.theme.colors.get(props.$fontColor)};
  width: 100%;
`;

export const HeadlineWrapper = styled.div`
  width: 100%;

  @media ${props => props.theme.laptop} {
    h2 {
      text-align: left;
    }

    > div {
      align-items: flex-start;
      justify-content: flex-start;
    }

    > div > div {
      justify-content: flex-start;
    }
  }
`;

export const DesktopCtaContainer = styled.div`
  display: none;
  width: 100%;

  @media ${props => props.theme.laptop} {
    display: block;
  }
`;

export const MobileCtaContainer = styled.div`
  display: flex;
  width: 100%;
  margin-top: ${props => props.theme.spacing(6)};

  & > * {
    width: 100%;
  }

  @media ${props => props.theme.laptop} {
    display: none;
  }
`;

export const CardsGrid = styled.div`
  display: none;

  @media ${props => props.theme.laptop} {
    display: grid;
    grid-template-columns: repeat(${props => props.$count}, minmax(0, 1fr));
    gap: ${props => props.theme.spacing(6)};
    width: 100%;
    /* Room for hover shadows without shifting layout */
    padding: ${props => props.theme.spacing(5, 4, 12)};
    margin: ${props => props.theme.spacing(-5, -4, -12)};
  }
`;

export const CardsScrollRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(4)};
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  /* Break out of section padding so cards can full-bleed while scrolling */
  margin-left: ${props => props.theme.spacing(-4)};
  margin-right: ${props => props.theme.spacing(-4)};
  /* Bottom padding leaves room for hover shadows */
  padding: ${props => props.theme.spacing(0, 0, 12)};
  /* Keep the last card from settling flush to the right edge */
  scroll-padding-right: ${props => props.theme.spacing(4)};

  &::-webkit-scrollbar {
    display: none;
  }

  ${props =>
    props.$centerSingleCard &&
    css`
      overflow-x: visible;
      justify-content: center;
      margin-left: 0;
      margin-right: 0;
      scroll-snap-type: none;
      scroll-padding-right: 0;
    `}

  @media ${props => props.theme.laptop} {
    display: ${props => (props.$desktopScroll ? 'flex' : 'none')};
    padding: ${props => props.theme.spacing(5, 4, 12)};
    margin: ${props => props.theme.spacing(-5, -4, -12)};
  }
`;

export const CardsScrollSpacer = styled.div`
  flex-shrink: 0;
  /* Width is 0; the row gap provides the content inset at both ends. */
  width: 0;
  ${props =>
    props.$snap &&
    css`
      scroll-snap-align: start;
    `}

  @media ${props => props.theme.laptop} {
    display: none;
  }
`;

export const MobileCardSlot = styled.div`
  flex: 0 0 78vw;
  scroll-snap-align: ${props => props.$snapAlign || 'center'};
  max-width: 78vw;

  ${props =>
    props.$centerSingleCard &&
    css`
      scroll-snap-align: none;
    `}

  @media ${props => props.theme.laptop} {
    flex: ${props => (props.$desktopScroll ? '0 0 32%' : 'none')};
    max-width: ${props => (props.$desktopScroll ? '32%' : 'none')};
    scroll-snap-align: ${props => (props.$desktopScroll ? 'start' : 'none')};
  }
`;

export const DesktopCarouselWrapper = styled.div`
  display: none;
  position: relative;

  @media ${props => props.theme.laptop} {
    display: ${props => (props.$enabled ? 'block' : 'none')};
  }
`;

export const CarouselViewport = styled.div`
  overflow: hidden;
  /* Keep hover shadows inside the clipped viewport */
  padding: ${props => props.theme.spacing(5, 4, 12)};
  margin: ${props => props.theme.spacing(-5, -4, -12)};
`;

export const CarouselTrack = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(6)};
`;

export const CarouselSlide = styled.div`
  flex: 0 0 calc((100% - ${props => props.theme.spacing(6)} * 2) / 2.15);
  min-width: 0;
`;

export const CardWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const interactiveCardStyles = css`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  text-decoration: none;
  color: inherit;
  border-radius: 20px;
  transition: box-shadow 300ms ease;
  ${props => props.theme.getFocusOutline('background.lightest')};
`;

export const CardFrame = styled.div`
  display: block;
  width: 100%;
  border-radius: 20px;
`;

export const CardSurface = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  aspect-ratio: 9 / 16;
  width: 100%;
`;

export const CardMedia = styled.div`
  position: absolute;
  inset: 0;
  transition: transform 500ms ease;
  pointer-events: none;

  mux-player,
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  }

  mux-player {
    position: absolute;
    inset: 0;
    z-index: 1;
    background: transparent;
    --controls: none;
    --play-button: none;
    --media-object-fit: cover;
  }
`;

export const CardMediaPlayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  opacity: ${props => (props.$visible ? 1 : 0)};
  transition: opacity 200ms ease;
`;

export const CardGradient = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 42%;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(0, 0, 0, 0.72) 100%
  );
`;

export const CardOverlayTop = styled.div`
  position: absolute;
  top: ${props => props.theme.spacing(4)};
  left: ${props => props.theme.spacing(4)};
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${props => props.theme.spacing(2)};
  pointer-events: none;
`;

export const CardHandleLink = styled.a`
  position: absolute;
  top: ${props =>
    props.$hasPill ? props.theme.spacing(12) : props.theme.spacing(4)};
  left: ${props => props.theme.spacing(4)};
  z-index: 2;
  ${props => props.theme.typography.label};
  color: ${props => props.theme.colors.text.lightest};
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }

  ${props => props.theme.getFocusOutline('background.lightest')};
`;

export const CardQuoteBlock = styled.blockquote`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  margin: 0;
  padding: ${props => props.theme.spacing(0, 4, 5)};
  max-width: 80%;
  pointer-events: none;
`;

export const CardQuote = styled.p`
  ${props => props.theme.typography.body};
  color: ${props => props.theme.colors.text.lightest};
  margin: 0 0 ${props => props.theme.spacing(1.5)};
`;

export const CardAttribution = styled.cite`
  ${props => props.theme.typography.body};
  font-style: normal;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
`;

export const PlayButtonOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1;
`;

export const PlayButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 300ms ease, background 300ms ease;

  svg {
    /* Optical centering: triangles read heavy on the left */
    transform: translateX(2px);
  }
`;

export const CardInteractive = styled.button`
  ${interactiveCardStyles}

  &:hover {
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.18);
  }

  &:hover ${CardMedia} {
    transform: scale(1.02);
  }

  &:hover ${PlayButton} {
    transform: scale(1.1);
    background: ${props => props.theme.colors.background.lightest};
  }
`;

export const ImageModalContent = styled.div`
  width: min(95vw, 480px);
  max-height: 90vh;

  img {
    width: 100%;
    height: auto;
    border-radius: 20px;
    display: block;
  }
`;

export const VideoModalContent = styled.div`
  width: min(95vw, 420px);
  max-height: 80vh;

  mux-player {
    width: 100%;
    height: auto;
    max-height: 80vh;
    aspect-ratio: 9 / 16;
    display: block;
  }

  @media ${props => props.theme.laptop} {
    width: min(95vw, 520px);
    max-height: 95vh;

    mux-player {
      max-height: 95vh;
    }
  }
`;
